import { useState } from 'react';
import { BarTrendChart, Panel, SegmentedControl } from '@bcecloud/portal-ui';
import { BreakdownPanel } from '@/components/report-bits';
import { LOSS_GROUP_LABELS, shortMonth, type LossGroup, type LossScore } from '@/lib/sales';

/** Whose losses the chart shows. */
export interface LossScope {
  readonly key: string;
  readonly label: string;
  readonly losses: LossScore;
}

const LOSS_VIEW = { count: 'count', share: 'share' } as const;
type LossView = (typeof LOSS_VIEW)[keyof typeof LOSS_VIEW];

const LOSS_VIEW_LABELS: Readonly<Record<LossView, string>> = {
  [LOSS_VIEW.count]: 'Deals lost',
  [LOSS_VIEW.share]: 'Share of losses',
};

const GROUPS = Object.keys(LOSS_GROUP_LABELS) as LossGroup[];
const NOT_GIVEN = 'Not given';
const PERCENT = 100;

/**
 * Why deals were lost, month by month: Closed Lost stacked by loss-reason
 * group over 12 months, as counts or as each month's share, beside every
 * Reason_Lost__c value over the same window. With more than one scope (the
 * team and each rep) a switch picks whose losses to show.
 */
export function LossReasons({ scopes }: { readonly scopes: readonly LossScope[] }): React.ReactElement | null {
  const [scopeKey, setScopeKey] = useState(scopes[0]?.key ?? '');
  const [view, setView] = useState<LossView>(LOSS_VIEW.count);
  const scope = scopes.find((entry) => entry.key === scopeKey) ?? scopes[0];
  if (!scope) return null;

  const { trend, reasons } = scope.losses;
  const valueOf = (total: number, count: number): number =>
    view === LOSS_VIEW.share ? (total === 0 ? 0 : (count / total) * PERCENT) : count;
  const series = GROUPS.map((group) => ({
    key: group,
    label: LOSS_GROUP_LABELS[group],
    values: trend.map((month) => valueOf(month.total, month.byGroup[group])),
  }));
  const points = trend.map((month) => ({
    key: month.month,
    label: shortMonth(month.month),
    value: view === LOSS_VIEW.share ? (month.total === 0 ? 0 : PERCENT) : month.total,
  }));
  const total = reasons.reduce((sum, row) => sum + row.count, 0);
  const title = `Why deals were lost${scopes.length > 1 ? `: ${scope.label}` : ''}`;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Panel className="p-5 lg:col-span-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-portal-card-title font-bold text-portal-ink">{title}</h2>
            <p className="text-portal-micro text-portal-muted">
              Closed Lost by loss reason, by the month the deal was closed. Deals that should be closed but are
              still open have no reason yet and are not shown.
            </p>
          </div>
          <SegmentedControl<LossView>
            label="Show deals lost or share of losses"
            value={view}
            onChange={setView}
            options={(Object.keys(LOSS_VIEW_LABELS) as LossView[]).map((key) => ({ key, label: LOSS_VIEW_LABELS[key] }))}
          />
        </div>
        {scopes.length > 1 ? (
          <div className="mt-3">
            <SegmentedControl<string>
              label="Whose losses to show"
              value={scope.key}
              onChange={setScopeKey}
              options={scopes.map((entry) => ({ key: entry.key, label: entry.label }))}
            />
          </div>
        ) : null}
        <BarTrendChart
          className="mt-4"
          title={title}
          unit={view === LOSS_VIEW.share ? 'of losses' : 'deals lost'}
          points={points}
          series={series}
          height={280}
          formatValue={(value) => (view === LOSS_VIEW.share ? `${Math.round(value)}%` : Math.round(value).toLocaleString('en-US'))}
        />
      </Panel>
      <BreakdownPanel
        title="Every loss reason"
        hint={`Last 12 months · ${total.toLocaleString('en-US')} deals lost`}
        rows={reasons.map((row) => ({ key: row.reason || NOT_GIVEN, leads: row.count, signed: 0 }))}
        total={total}
      />
    </div>
  );
}
